import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { getTheme, applyThemeToDocument, ThemeName, ThemeColors } from '../lib/theme';

interface ThemeContextType {
  currentTheme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  theme: ThemeColors;
  availableThemes: ThemeName[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Charger le thème depuis localStorage immédiatement pour éviter le flash
  const getInitialTheme = (): ThemeName => {
    if (typeof window === 'undefined') return 'dark';
    try {
      const saved = localStorage.getItem('theme');
      if (saved && ['light', 'dark', 'blue', 'purple', 'green'].includes(saved)) {
        return saved as ThemeName;
      }
    } catch {}
    return 'dark';
  };

  const [currentTheme, setCurrentTheme] = useState<ThemeName>(getInitialTheme());
  const { user } = useAuth();
  const theme = getTheme(currentTheme);

  // Appliquer le thème au document immédiatement et lors des changements
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && typeof document !== 'undefined' && applyThemeToDocument) {
        applyThemeToDocument(theme);
        const root = document.documentElement;
        if (root) {
          root.classList.remove('dark', 'light', 'blue', 'purple', 'green');
          root.classList.add(currentTheme);
          root.setAttribute('data-theme', currentTheme);
          localStorage.setItem('theme', currentTheme);
        }
      }
    } catch (error) {
      console.error('Erreur lors de l\'application du thème:', error);
    }
  }, [currentTheme, theme]);

  // Charger le thème depuis la base de données
  useEffect(() => {
    const loadTheme = async () => {
      if (!user || !supabase) return;
      
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('theme')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (error) {
          if (error.code !== 'PGRST116') {
            console.warn('Erreur lors du chargement du thème (peut être normal si la colonne n\'existe pas):', error);
          }
          return;
        }
        
        if (data?.theme && ['light', 'dark', 'blue', 'purple', 'green'].includes(data.theme)) {
          setCurrentTheme(data.theme as ThemeName);
        }
      } catch (error) {
        console.warn('Erreur lors du chargement du thème:', error);
      }
    };

    loadTheme();
  }, [user]);

  // Sauvegarder le thème en base de données
  const setTheme = async (themeName: ThemeName) => {
    setCurrentTheme(themeName);
    localStorage.setItem('theme', themeName);
    
    if (user && supabase) {
      try {
        const { error } = await supabase
          .from('user_profiles')
          .update({ theme: themeName })
          .eq('user_id', user.id);
        
        if (error) {
          if (error.code !== 'PGRST116' && error.code !== 'PGRST406') {
            console.warn('Erreur lors de la sauvegarde du thème (peut être normal si la colonne n\'existe pas):', error);
          }
        }
      } catch (error) {
        console.warn('Erreur lors de la sauvegarde du thème:', error);
      }
    }
  };

  const value = {
    currentTheme,
    setTheme,
    theme,
    availableThemes: ['light', 'dark', 'blue', 'purple', 'green'] as ThemeName[],
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};